<template>
  <VCard v-if="roomId && !isCallFound" class="mt-4 p-4 flex justify-center">
    <LoaderElement type="circular" size="large" />
  </VCard>

  <VCard v-if="showCard && callFrameSrc" class="mt-4 max-w-[1200px]">
    <iframe :src="callFrameSrc" allow="camera *; microphone *; display-capture *" />

    <CallFrameDetails
      v-if="isInScheduledCall && scheduledConsultationDTO"
      :cases="cases"
      :custom-fields="scheduledConsultationDTO.customFields"
      :details="{
        type: 'scheduled',
        comment: scheduledConsultationDTO.patientPresentationComment,
        medicalHistory: scheduledConsultationDTO.patientPresentationMedicalHistory,
        conferenceTitle: scheduledConsultationDTO.conferenceTopic,
        conferenceDescription: scheduledConsultationDTO.conferenceDescription
      }"
      v-model:show-details="showDetails"
    />

    <CallFrameDetails
      v-if="isInDirectCall && directConsultation"
      :cases="[directConsultation.case]"
      :custom-fields="directConsultation.customFields"
      :details="{
        type: 'direct',
        diagnosis: directConsultation.diagnosis,
        question: directConsultation.question,
        medicalHistory: directConsultation.medicalHistory,
        therapy: directConsultation.therapy
      }"
      v-model:show-details="showDetails"
    />

    <VCard
      class="flex flex-col md:flex-row gap-2 items-center flex-wrap justify-between flex-1 ml-1 p-4 pt-2"
      flat
    >
      <template v-if="isInDirectCall && !isJoinConsultationPath && consultationShareCode">
        <p class="text-h6">{{ t('Invite more participants:') }}</p>
        <div class="flex items-center gap-2 border border-gray-300 max-w-full">
          <VTextField
            v-model="consultationShareCode"
            disabled
            hide-details="auto"
            variant="solo"
            :style="{ width: `${consultationShareCode.length}ch` }"
          />
          <VBtn variant="plain" @click="copyShareCodeToClipboard">
            <VIcon icon="content_copy" class="text-secondary-text" />
          </VBtn>
        </div>
      </template>

      <template
        v-if="isInScheduledCall && !isJoinConsultationPath && scheduledConsultationShareCode"
      >
        <p class="text-h6">{{ t('Invite more participants:') }}</p>
        <div class="flex items-center gap-2 border border-gray-300 max-w-full">
          <VTextField
            v-model="scheduledConsultationShareCode"
            disabled
            hide-details="auto"
            variant="solo"
            :style="{ width: `${scheduledConsultationShareCode.length}ch` }"
          />
          <VBtn variant="plain" @click="copyShareCodeToClipboard">
            <VIcon icon="content_copy" class="text-secondary-text" />
          </VBtn>
        </div>
      </template>

      <template v-if="isInScheduledCall && scheduledConsultationDTO">
        <div class="flex items-center gap-1 max-w-full">
          <span class="label text-h6">
            {{ translateScheduledConsultationTypeDTO(scheduledConsultationDTO.type, t) }}:
          </span>
          <span class="value"
            >{{ formatDateTime(scheduledConsultationDTO.scheduledStartTime) }}
            {{ t("o'clock") }}</span
          >
        </div>
      </template>

      <VBtn
        :text="t('Legal Notice')"
        :href="'https://arztkonsultation.de/impressum/'"
        variant="plain"
        :ripple="false"
        target="_blank"
        class="text-body-2 p-0 ml-auto"
      />
    </VCard>
  </VCard>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useCallStore } from '@/stores/call'
import { storeToRefs } from 'pinia'
import { getAkBaseUrl } from '@/global/environmentGetters'
import { useNoticeStore } from '@/stores/notice'
import { NoticeType } from '@/stores/models/Notice'
import { useRoute, useRouter } from 'vue-router'
import { routeNames } from '@/router/routeNames'
import { useI18n } from 'vue-i18n'
import LoaderElement from '@/atoms/LoaderElement.vue'
import {
  useConsultationActivity,
  type ConsultationType
} from '@/composables/useConsultationActivity'
import { useRefreshGuard } from '@/composables/useRefreshGuard'
import CallFrameDetails from '@/atoms/CallFrameDetails.vue'
import { handleScrollToTopWhenInCall } from '@/util/callFrame.utils'
import { useFormatDate } from '@/composables/useFormatDate'
import { translateScheduledConsultationTypeDTO } from '@/util/translations'

const { t, locale } = useI18n()
const { formatDateTime } = useFormatDate()
const route = useRoute()
const router = useRouter()

const {
  callConsultationRequestId,
  isInCall,
  isInDirectCall,
  isInScheduledCall,
  roomId,
  accessToken,
  consultationShareCode,
  scheduledConsultationShareCode,
  scheduledConsultation,
  directConsultationRequest
} = storeToRefs(useCallStore())

const {
  joinCallWithConsultationShareCode,
  getConsultationShareCode,
  getScheduledConsultationShareCode,
  leaveCall,
  tryRestoreActiveCall
} = useCallStore()

const { add: addNotice } = useNoticeStore()

const showDetails = ref(false)
const isCallFound = ref(false)
const callFrameSrc = ref<string | null>(null)

const isJoinConsultationPath = computed(() => route.name === 'join-consultation')
const isScheduledCallPath = computed(() => route.name === routeNames.SCHEDULED_CALL)
const isSharedCodePresent = computed(() => route.query.shareCode !== undefined)

const showCard = computed<boolean>(() => isInCall.value)
const scheduledConsultationDTO = computed(() => scheduledConsultation.value)
const directConsultation = computed(() => directConsultationRequest.value)
const cases = computed(() => scheduledConsultation.value?.cases ?? [])

onMounted(() => window.addEventListener('message', handleMessageEvent))
onUnmounted(() => window.removeEventListener('message', handleMessageEvent))
useRefreshGuard(() => showCard.value && !!callFrameSrc.value)

watch(
  () => locale.value,
  async () => {
    await tryRestoreActiveCall()
    callFrameSrc.value = createCallFrameUrl()
  }
)

watch(
  callConsultationRequestId,
  async () => {
    if (callConsultationRequestId.value && isInDirectCall.value && !isJoinConsultationPath.value) {
      await getConsultationShareCode(callConsultationRequestId.value)
    }
    if (
      callConsultationRequestId.value &&
      isInScheduledCall.value &&
      !isJoinConsultationPath.value &&
      !isSharedCodePresent.value
    ) {
      await getScheduledConsultationShareCode(callConsultationRequestId.value)
    }

    callFrameSrc.value = createCallFrameUrl()
  },
  { immediate: true }
)

const currentConsultationType = computed<ConsultationType>(() =>
  isInScheduledCall.value ? 'SCHEDULED' : 'DIRECT'
)

useConsultationActivity(isCallFound, currentConsultationType)

watch([showCard, callFrameSrc], () => {
  if (showCard.value && callFrameSrc.value) {
    handleScrollToTopWhenInCall()

    if (isInScheduledCall.value || isInDirectCall.value) {
      isCallFound.value = true
    }
  }
})

watch(
  () => route.params.consultationShareCode,
  (newCode) => {
    if (isJoinConsultationPath.value && newCode) {
      joinCallWithConsultationShareCode(newCode as string).then(() => {
        if (!callConsultationRequestId.value) {
          router.push({ name: routeNames.CONSULTATIONS })
        } else {
          callFrameSrc.value = createCallFrameUrl()
        }
      })
    }
  }
)

function createCallFrameUrl() {
  if (!showCard.value) {
    return null
  }

  if (!accessToken.value) {
    handleAccessTokenError()
    return null
  }

  const base64AccessToken = btoa(accessToken.value)
  const url = new URL(`video/${roomId.value}?tid=${base64AccessToken}`, getAkBaseUrl())
  url.searchParams.set('lang', locale.value)

  return url.toString()
}

function handleAccessTokenError() {
  addNotice({
    type: NoticeType.ERROR,
    message: t('Failed to join the call, because of invalid access token')
  })
  if (isJoinConsultationPath.value) {
    router.push({ name: routeNames.CONSULTATIONS })
  }
}

function copyShareCodeToClipboard() {
  if (consultationShareCode.value) {
    navigator.clipboard.writeText(consultationShareCode.value)
  } else if (scheduledConsultationShareCode.value) {
    navigator.clipboard.writeText(scheduledConsultationShareCode.value)
  }
}

function handleMessageEvent(msg: MessageEvent<string>) {
  if (msg.data === 'CALL_FOUND') {
    isCallFound.value = true
  }

  if (msg.data === 'CALL_CLOSED') {
    leaveCall()
    if (isJoinConsultationPath.value || isScheduledCallPath.value) {
      router.push({ name: routeNames.CONSULTATIONS })
    }
    return
  }

  if (msg.data === 'CALL_NOT_FOUND') {
    addNotice({
      type: NoticeType.ERROR,
      message: t('Failed to join the call')
    })

    if (isJoinConsultationPath.value || isScheduledCallPath.value) {
      router.push({ name: routeNames.CONSULTATIONS })
    }
    leaveCall()
    return
  }
}
</script>

<style scoped>
iframe {
  width: 100%;
  height: 750px;
  border: none;
}
</style>
